import { task } from 'nanostores'
import Cookie from 'js-cookie'

import {
  authentication,
  fetchCustomerSubscriptionCampaigns,
  getCustomerSubscriptions,
  updateCustomer,
} from '@/clients/pops/client'
import {
  $authenticationToken,
  $customer,
  $promises,
  $subscriptionIdToCampaingsMap,
  $subscriptions,
} from './stores'
import { setCustomerInfo, setSubscriptions } from './actions'
import { loadDeliveryZoneById } from '../delivery-zone/operations'
import { loadDiscountOptionWeeks } from '../discount-option/operations'
import type { Campaign } from '@/models/campaign'
import { createCampaign, createSubscriptions } from '@/utilities/factories'
import { getCustomer, loginCustomer } from '@/clients/shopify/client'
import {
  ENCODED_CUSTOMER_ID,
  USER_ACCESS_POPS_TOKEN,
  USER_ACCESS_TOKEN,
} from '@/constants/shopify'
import { chord } from '@/scripts/tracking/chord'
import { generateShaHash } from '@/utilities/crypto'

const DEFAULT_SUBSCRIPTIONS_PAGE = 1
const DEFAULT_SUBSCRIPTIONS_PER_PAGE = 100

export const loadSubscriptions = async ({
  page = DEFAULT_SUBSCRIPTIONS_PAGE,
  perPage = DEFAULT_SUBSCRIPTIONS_PER_PAGE,
  sortBy,
  filterBy,
  force = false,
}: {
  page?: number
  perPage?: number
  sortBy?: string
  filterBy?: string
  force?: boolean
}) => {
  const promiseKey = `loadSubscriptionsPage${page}PerPage${perPage}SortBy${sortBy}FilterBy${filterBy}`

  if ($promises.get()[promiseKey] && !force) {
    return $promises.get()[promiseKey]
  }

  const customer = $customer.get()

  if (!customer?.simpleId) {
    return
  }

  const promise = (async () => {
    const rawSubscriptions = await getCustomerSubscriptions(customer.simpleId, {
      page,
      perPage,
      sortBy,
      filterBy,
    })

    setSubscriptions({
      subscriptions: createSubscriptions(rawSubscriptions),
      force,
    })

    await Promise.all(
      $subscriptions.get().reduce((promises, subscription) => {
        promises.push(
          loadDeliveryZoneById({ deliveryZoneId: subscription.deliveryZoneId }),
          loadDiscountOptionWeeks({ subscriptionId: subscription.id }),
        )

        return promises
      }, [] as Promise<any>[]),
    )
  })()

  $promises.setKey(promiseKey, promise)

  return promise
}

export const loadSubscriptionsForRewards = async () => {
  const promiseKey = `loadSubscriptions_rewards`

  if ($promises.get()[promiseKey]) {
    return $promises.get()[promiseKey]
  }

  const customer = $customer.get()
  if (!customer?.simpleId) {
    return
  }

  const promise = (async () => {
    const rawSubscriptions = await getCustomerSubscriptions(customer.simpleId, {
      filterBy: 'omd',
      perPage: DEFAULT_SUBSCRIPTIONS_PER_PAGE,
    })

    setSubscriptions({
      subscriptions: createSubscriptions(rawSubscriptions),
      force: true,
    })
  })()

  $promises.setKey(promiseKey, promise)

  return promise
}

export const loadSubscriptionCampaigns = async ({
  customerId,
  force = false,
}: {
  customerId: number
  force?: boolean
}) => {
  const promiseKey = 'loadSubscriptionCampaigns'

  if ($promises.get()[promiseKey] && !force) {
    return $promises.get()[promiseKey]
  }

  const promise = (async () => {
    const rawSubscriptionCampaigns =
      await fetchCustomerSubscriptionCampaigns(customerId)
    const subscriptionIdToCampaignsMap = rawSubscriptionCampaigns.reduce(
      (map, { subscription_id, campaigns }) => {
        map[subscription_id] = campaigns.reduce(
          (parsedCampaigns, rawCampaign) => {
            const campaign = createCampaign(rawCampaign)

            if (campaign) {
              parsedCampaigns.push(campaign)
            }

            return parsedCampaigns
          },
          [] as Campaign[],
        )
        return map
      },
      {} as Record<string, Campaign[]>,
    )

    $subscriptionIdToCampaingsMap.set(subscriptionIdToCampaignsMap)
  })()

  $promises.setKey(promiseKey, promise)

  return promise
}

export const saveContactInfo = async ({
  email,
  firstName,
  lastName,
}: {
  email: string
  firstName: string
  lastName: string
}) => {
  try {
    const customerObject = $customer.get()
    if (!customerObject) {
      return
    }

    await updateCustomer(customerObject.simpleId, email, firstName, lastName)
    setCustomerInfo({
      id: customerObject.id,
      email,
      firstName,
      lastName,
    })
  } catch (error) {
    // Save error
  }
}

const setEncodedCustomerId = (customerId: string) => {
  const encodedCustomerId = btoa(customerId)
  Cookie.set(ENCODED_CUSTOMER_ID, encodedCustomerId, { expires: 10 })
}

const setPopsAuthToken = async () => {
  const popsAuthResponse = await authentication()
  const popsToken = popsAuthResponse?.auth_token?.token
  Cookie.set(USER_ACCESS_POPS_TOKEN, popsToken, {
    expires: popsAuthResponse?.auth_token?.expires_at,
  })
}

const getCustomerSubscriberStatus = async (customer: any) => {
  try {
    const customerId = Number(customer.id.split('/').pop())
    const activeMealProgramSubscriptions = await getCustomerSubscriptions(
      customerId,
      {
        page: 1,
        perPage: 1,
        filterBy: 'omd,active',
        skipLogoutWhenNotAuthorized: true,
      },
    )

    return activeMealProgramSubscriptions.length > 0
  } catch (error) {
    console.warn('It was not possible to fetch customer subscriber status')
    return false
  }
}

const setCustomerDetails = async (accessToken: string) => {
  const customer = await getCustomer(accessToken)

  setEncodedCustomerId(customer.id)

  await setPopsAuthToken()

  const isSubscriber = await getCustomerSubscriberStatus(customer)
  const emailHash = await generateShaHash(customer.email)
  const emailSha1Hash = await generateShaHash(customer.email, 'SHA-1')

  setCustomerInfo({
    id: customer.id,
    email: customer.email,
    firstName: customer.firstName,
    lastName: customer.lastName,
    emailHash,
    emailSha1Hash,
    isSubscriber,
  })
}

export const tryLogin = async (email: string, password: string) => {
  return task(async () => {
    try {
      const response = await loginCustomer(email, password)
      const accessToken = response?.accessToken
      if (accessToken) {
        if (chord) {
          await chord.trackSignedIn({ email })
          chord.identify({ email })
        }
        Cookie.set(USER_ACCESS_TOKEN, accessToken, { expires: 10 })

        await setCustomerDetails(accessToken)
        $authenticationToken.set(accessToken)
        return true
      }
    } catch (error) {
      console.log(error)
    }
  })
}

export const tryLogout = async () => {
  return await task(async () => {
    const customerEmail = $customer.get()?.email

    Cookie.remove(USER_ACCESS_TOKEN)
    $authenticationToken.set('')
    $customer.set(null)

    if (customerEmail && chord) {
      await chord.trackSignedOut({ email: customerEmail })
      chord.reset()
    }
    return true
  })
}
